<template>
  <div>
    <PageHeader title="Factures sinistres">
      <div v-if="selectedRows.length > 0" class="flex gap-x-2 items-center">
        <SecondaryButton @click="markAsPaid">Marquer comme payé</SecondaryButton>
        <SecondaryButton @click="exportTransfers">Exporter les virements</SecondaryButton>
      </div>
    </PageHeader>
    <Alert v-if="amountDue > 0" type="info" class="mb-4">
      <template v-if="selectedRows.length > 0">
        Le montant sélectionné restant à payer s'élève à {{ $n(selectedAmountDue, "currency") }}
      </template>
      <template v-else>Le montant total restant à payer s'élève à {{ $n(amountDue, "currency") }}</template>
    </Alert>
    <SearchToolbar
      class="mb-2"
      placeholder="Rechercher une facture de sinistre"
      :initial-keywords="keywords"
      @update-keyword-search="updateKeywordSearch"
    >
      <DateRangePicker v-model="date" placeholder="Veuillez sélectionner la période de résolution de sinistre" />
    </SearchToolbar>
    <div class="mb-6 flex items-center gap-x-4 flex-wrap gap-5">
      <MultiSelect
        name="organization"
        label="Organisation"
        :options="organizations"
        v-model="organization"
        :searchKeywordThreshold="0"
        @keyword="(keywords) => fetchOrganizations({ keywords })"
      />
      <MultiSelect
        name="store"
        label="Point de vente"
        :options="stores"
        v-model="store"
        :searchKeywordThreshold="0"
        @keyword="
          (keywords) => fetchStores({ keywords, organizationId: organization, perPage: 2000, status: ['active'] })
        "
      />
      <MultiSelect
        name="handlingType"
        label="Type de prise en charge"
        :options="options.handlingType"
        :enable-select-all="false"
        v-model="handlingType"
      />
      <MultiSelect name="paid" label="Payé" :options="options.paid" :enable-select-all="false" v-model="paid" />
      <MultiSelect name="insurer" label="Assureur" :options="insurers" :enable-select-all="false" v-model="insurer" />
      <MultiSelect
        name="vertical"
        label="Verticale"
        :options="contractDistributions"
        :enable-select-all="false"
        v-model="vertical"
      />
      <MultiSelect
        name="refundedEntity"
        label="Bénéficiaire"
        :options="options.refundedEntity"
        :enable-select-all="false"
        v-model="refundedEntity"
      />
      <ClearFiltersButton v-if="hasActiveFilters" @click="clearFilters" />
    </div>
    <Table
      :columns="columns"
      :data="claimInvoices"
      :isDownloadable="true"
      :isAccessible="true"
      :isSelectable="true"
      :selectableRowFilter="selectableRowFilter"
      @download="openInNewTab"
      @view="rowClick"
      @sort="sortBy"
      @newSelectedRows="updateSelectedRows"
    >
      <template #friendlyId="{ row }">
        <TableLink :to="`/claims/${row.friendlyId}`" data-test="claim-link">
          {{ row.friendlyId }}
        </TableLink>
        <div v-if="row.claim?.customer?.email" class="mt-1 text-gray-500">
          {{ row.claim.customer.email }}
        </div>
      </template>
      <template #store="{ row }">
        <TableLink :to="`/stores/${row.storeId}`" data-test="store-link">
          {{ row.store }}
        </TableLink>
      </template>
      <template #refundedEntity="{ row }">
        <div class="flex">
          <div class="rounded-lg">
            <span
              :class="[
                row.refundedEntity === 'customer' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800',
                'inline-block items-center px-3 py-0.5 mt-1 rounded-full text-xs font-medium',
              ]"
            >
              {{ $t(row.refundedEntity) }}
            </span>
          </div>
        </div>
      </template>
      <template #cost="{ row }">
        <div class="flex gap-x-6">
          <div class="flex-auto">
            <div class="flex items-start gap-x-3">
              <div class="font-medium text-gray-900">{{ $n(row.repairCost || 0, "currency") }}</div>
            </div>
            <div v-if="row.claimCost !== row.repairCost" class="mt-1 text-gray-500">
              <span class="font-bold">Coût total sinistre :</span>
              {{ $n(row.claimCost || 0, "currency") }}
            </div>
          </div>
        </div>
      </template>
      <template #paid="{ row }">
        <div class="flex gap-x-6">
          <div class="flex-auto">
            <div class="flex items-start gap-x-3">
              <div
                :class="[
                  row.paid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800',
                  'rounded-md px-2 text-xs font-medium',
                ]"
              >
                {{ row.paid ? $t("paid") : $t("Pending payment") }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </Table>
    <Pagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      @changePage="fetchClaimInvoices"
    />
  </div>
</template>

<script>
import {
  Alert,
  DateRangePicker,
  Pagination,
  SecondaryButton,
  Table,
  MultiSelect,
  PageHeader,
  SearchToolbar,
  ClearFiltersButton,
  download,
  isPresent,
  TableLink,
} from "@estaly/ui";
import { toCamelCase } from "@/utils/utils";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    Alert,
    DateRangePicker,
    SecondaryButton,
    PageHeader,
    Pagination,
    SearchToolbar,
    ClearFiltersButton,
    Table,
    MultiSelect,
    TableLink,
  },
  data() {
    return {
      keywords: null,
      store: [],
      organization: [],
      handlingType: [],
      paid: [],
      insurer: [],
      vertical: [],
      refundedEntity: [],
      options: {
        handlingType: [
          { value: "voucher", label: "Bon d'achat" },
          { value: "reimbursement", label: "Remboursement" },
          { value: "replacement", label: "Remplacement" },
          { value: "accessory_replacement", label: "Remplacement d'accessoire" },
          { value: "repair", label: "Réparation" },
        ],
        paid: [
          { label: "Oui", value: true },
          { label: "Non", value: false },
        ],
        refundedEntity: [
          { label: "Marchant", value: "merchant" },
          { label: "Client", value: "customer" },
        ],
      },
      claimInvoices: [],
      amountDue: null,
      selectedRows: [],
      startDate: null,
      endDate: null,
      currentPage: 1,
      totalCount: null,
      totalPages: null,
      sortColumn: null,
      sortOrder: null,
      order: null,
      columns: [
        { label: "Sinistres", field: "friendlyId", slot: "friendlyId" },
        { label: "Date de résolution", field: "resolutionDate", sortable: true },
        { label: "Type", field: "handlingType" },
        { label: "Magasin", field: "store", slot: "store" },
        { label: "Bénéficiaire", field: "refundedEntity", slot: "refundedEntity" },
        { label: "Montant facture", slot: "cost", field: "repairCost" },
        { label: "Statut", slot: "paid", field: "paid" },
        { label: "Date de paiement", field: "paymentDate" },
      ],
    };
  },
  computed: {
    selectedAmountDue() {
      return this.selectedRows.reduce((total, row) => total + (row.repairCost || 0), 0);
    },
    ...mapGetters("organizations", { organizationsIndex: "getIndex" }),
    ...mapGetters("stores", { storesIndex: "getIndex" }),
    ...mapGetters("insurers", { insurersIndex: "getIndex" }),
    ...mapGetters("contractDistributions", { contractDistributionsIndex: "getIndex" }),
    hasActiveFilters() {
      return (
        [
          this.store,
          this.organization,
          this.handlingType,
          this.paid,
          this.insurer,
          this.vertical,
          this.refundedEntity,
        ].some((filter) => filter.length > 0) || Boolean(this.startDate)
      );
    },
    params() {
      return {
        keywords: this.keywords,
        order: this.order,
        startDate: this.startDate,
        endDate: this.endDate,
        store: this.store,
        organization: this.organization,
        handlingType: this.handlingType,
        paid: this.paid,
        insurer: this.insurer,
        vertical: this.vertical,
        refundedEntity: this.refundedEntity,
        approvalStatus: "approved",
        claimStatus: "resolved",
        includes: ["claim", "claim.customer"],
      };
    },
    date: {
      get() {
        return [this.startDate, this.endDate];
      },
      set(value) {
        if (value && value.length === 2) {
          this.startDate = value[0];
          this.endDate = value[1];
        } else {
          this.startDate = null;
          this.endDate = null;
        }
      },
    },
    organizations() {
      return this.organizationsIndex.map((organization) => {
        return {
          label: organization.name,
          value: organization.id,
        };
      });
    },
    stores() {
      return this.storesIndex
        .filter((store) => {
          return this.organization.length === 0 || this.organization.includes(store.organizationId);
        })
        .map((store) => {
          return {
            label: store.salesChannel,
            value: store.id,
          };
        });
    },
    insurers() {
      return this.insurersIndex.map((insurer) => {
        return {
          label: insurer.name,
          value: insurer.id,
        };
      });
    },
    contractDistributions() {
      return this.contractDistributionsIndex.map((contractDistribution) => {
        return {
          label: contractDistribution.translatedCategory,
          value: contractDistribution.category,
        };
      });
    },
  },
  async mounted() {
    this.fetchOrganizations({ perPage: 2000 });
    this.fetchStores({ perPage: 2000, status: ["active"] });
    this.fetchInsurers();
    this.fetchContractDistributions();
    this.fetchClaimInvoices();
  },
  watch: {
    date() {
      this.fetchClaimInvoices();
    },
    organization() {
      this.fetchClaimInvoices();
      this.fetchStores({ organizationId: this.organization, perPage: 2000, status: ["active"] });
    },
    store() {
      this.fetchClaimInvoices();
    },
    handlingType() {
      this.fetchClaimInvoices();
    },
    paid() {
      this.fetchClaimInvoices();
    },
    insurer() {
      this.fetchClaimInvoices();
    },
    vertical() {
      this.fetchClaimInvoices();
    },
    refundedEntity() {
      this.fetchClaimInvoices();
    },
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("organizations", { fetchOrganizations: "fetchIndex" }),
    ...mapActions("stores", { fetchStores: "fetchIndex" }),
    ...mapActions("insurers", { fetchInsurers: "fetchIndex" }),
    ...mapActions("contractDistributions", { fetchContractDistributions: "fetchIndex" }),
    ...mapActions("claims", ["fetchBillingInvoices", "markInvoicesAsPaid", "getInvoicesTransfers"]),
    ...mapActions("backoffice", ["withLoader"]),

    clearFilters() {
      this.store = [];
      this.organization = [];
      this.handlingType = [];
      this.paid = [];
      this.insurer = [];
      this.vertical = [];
      this.refundedEntity = [];
      this.date = [];
    },

    async fetchClaimInvoices(page = 1) {
      if (this.sortColumn) {
        const direction = this.sortOrder === "ASC" ? "" : "-";
        const columnEquivalent = this.sortColumn === "resolutionDate" ? "claim.resolution_date" : this.sortColumn;
        this.order = `${direction}${columnEquivalent}`;
      }

      await this.withLoader(async () => {
        const response = await this.fetchBillingInvoices({ page, ...this.params });
        if (!response?.success) return;

        const formattedResponse = toCamelCase(response.data);
        this.claimInvoices = formattedResponse.claimInvoices;
        this.amountDue = formattedResponse.amountDue;
        this.totalCount = formattedResponse.page.totalCount;
        this.totalPages = formattedResponse.page.totalPages;
        this.currentPage = formattedResponse.page.currentPage;
      });
    },
    openInNewTab(row) {
      window.open(row.url, "_blank");
    },
    rowClick(row) {
      window.open(`/claims/${row.friendlyId}`, "_blank");
    },
    sortBy(column) {
      if (!isPresent(this.claimInvoices)) return;
      this.sortColumn = column.field;
      this.sortOrder = this.sortOrder === "ASC" ? "DESC" : "ASC";
      this.fetchClaimInvoices();
    },
    updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.fetchClaimInvoices();
    },
    updateSelectedRows(newSelectedRows) {
      this.selectedRows = newSelectedRows;
    },
    selectableRowFilter(row) {
      return row.paid === false;
    },
    async markAsPaid() {
      const claimInvoiceIds = this.selectedRows.map((row) => row.id);
      if (claimInvoiceIds.length === 0) return;
      const response = await this.markInvoicesAsPaid(claimInvoiceIds);
      if (!response?.success) return;
      await this.fetchClaimInvoices();
    },
    async exportTransfers() {
      const claimInvoiceIds = this.selectedRows.map((row) => row.id);
      if (claimInvoiceIds.length === 0) return;
      const response = await this.getInvoicesTransfers(claimInvoiceIds);
      if (!response?.success) return;

      download(response.data, "claim_invoices_transfers.csv");
      this.notify({
        category: "simple",
        type: "success",
        title: "Exporting claim invoices transfers file.",
      });
    },
  },
};
</script>
